import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api';

const fmtMoney = v => `$${v >= 0.01 || v === 0 ? v.toFixed(2) : v.toFixed(4)}`;
const fmt = ts => ts
  ? new Date(ts).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';

const STATUS_FILTERS = [
  { key: 'all',       label: 'All',          match: () => true },
  { key: 'on_hold',   label: '⏸ On hold',   match: r => r.status === 'on_hold' },
  { key: 'sent',      label: '📤 Sent',      match: r => ['sent', 'delivered', 'read'].includes(r.status) },
  { key: 'delivered', label: '✅ Delivered', match: r => ['delivered', 'read'].includes(r.status) },
  { key: 'read',      label: '👁️ Read',      match: r => r.status === 'read' },
  { key: 'clicked',   label: '🔗 Clicked',   match: r => r.clicked },
  { key: 'failed',    label: '❌ Failed',    match: r => r.status === 'failed' },
  { key: 'pending',   label: '⏳ Pending',   match: r => r.status === 'pending' },
];

const STATUS_BADGE = {
  pending: 'badge-gray', on_hold: 'badge-yellow', sent: 'badge-blue', delivered: 'badge-green',
  read: 'badge-yellow', failed: 'badge-red',
};

export default function BroadcastDetail() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [rb, rr] = await Promise.all([
        api.get(`/broadcasts/${id}`),
        api.get(`/broadcasts/${id}/recipients`),
      ]);
      setB(rb.data);
      setRecipients(rr.data.recipients || []);
    } catch {
      toast.error('Could not load broadcast');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function exportCsv() {
    setExporting(true);
    try {
      const r = await api.get(`/broadcasts/${id}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(b?.name || 'broadcast').replace(/[^\w-]+/g, '_')}_report.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch {
      toast.error('Export failed');
    }
    setExporting(false);
  }

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>Loading broadcast…</div>;
  if (!b) return (
    <div className="empty-state" style={{ padding: 60 }}>
      <div className="empty-state-icon">📢</div>
      <div className="empty-state-text">Broadcast not found</div>
      <Link to="/broadcasts" className="btn btn-primary btn-sm" style={{ marginTop: 16 }}>← Back to broadcasts</Link>
    </div>
  );

  const s = b.stats || {};
  const statusClass = {
    draft: 'badge-gray', scheduled: 'badge-yellow', queued: 'badge-yellow',
    sending: 'badge-yellow', on_hold: 'badge-yellow', sent: 'badge-green', failed: 'badge-red',
  };

  const activeFilter = STATUS_FILTERS.find(f => f.key === filter) || STATUS_FILTERS[0];
  const q = search.trim().toLowerCase();
  const visible = recipients
    .filter(activeFilter.match)
    .filter(r => !q
      || (r.contact?.name || '').toLowerCase().includes(q)
      || (r.contact?.phone || '').toLowerCase().includes(q)
      || (r.contact?.email || '').toLowerCase().includes(q));

  const cost = b.pricing?.estimated_total > 0
    ? (['sending', 'sent'].includes(b.status) && s.sent ? s.sent * b.pricing.avg_rate : b.pricing.estimated_total)
    : null;

  const tiles = [
    { label: 'Total', value: b.total_count || 0 },
    { label: '⏸ On hold', value: s.on_hold || 0, key: 'on_hold' },
    { label: '📤 Sent', value: s.sent || 0, key: 'sent' },
    { label: '✅ Delivered', value: s.delivered || 0, key: 'delivered' },
    { label: '👁️ Read', value: s.read || 0, key: 'read' },
    { label: '🔗 Clicked', value: s.clicked || 0, key: 'clicked' },
    { label: '❌ Failed', value: s.failed || 0, key: 'failed' },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <Link to="/broadcasts" style={{ color: 'var(--text-muted)', fontSize: 13, textDecoration: 'none' }}>← Broadcasts</Link>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
            <span>{b.channel === 'whatsapp' ? '📱' : '📧'}</span>
            {b.name}
            <span className={`badge ${statusClass[b.status]}`}>{b.status}</span>
          </h1>
        </div>
        <button className="btn btn-primary" onClick={exportCsv} disabled={exporting || recipients.length === 0}>
          {exporting ? 'Exporting…' : '⬇ Export CSV'}
        </button>
      </div>

      {b.send_error && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid var(--danger)', borderRadius: 8, fontSize: 13 }}>
          <strong style={{ color: 'var(--danger)' }}>❌ Error:</strong>
          <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>{b.send_error}</span>
        </div>
      )}

      {/* Overview + content */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="card">
          <div className="card-title">Overview</div>
          <DetailRow label="Channel" value={b.channel === 'whatsapp' ? '📱 WhatsApp' : '📧 Email'} />
          <DetailRow label="Audience" value={
            b.all_contacts ? 'All opted-in contacts'
              : b.tags?.length ? b.tags.map(t => t.name).join(', ')
              : 'All opted-in contacts'
          } />
          <DetailRow label="Recipients" value={b.total_count || 0} />
          <DetailRow label="Created" value={fmt(b.createdAt)} />
          {b.scheduled_at && <DetailRow label="Scheduled for" value={fmt(b.scheduled_at)} />}
          <DetailRow label="Sent at" value={fmt(b.sent_at)} />
          {cost !== null && (
            <DetailRow label="Est. cost" value={`${fmtMoney(cost)} (~${fmtMoney(b.pricing.avg_rate)}/message)`} />
          )}
        </div>

        <div className="card">
          <div className="card-title">Content</div>
          {b.channel === 'whatsapp' ? (
            <>
              <DetailRow label="Template" value={b.template_name || '—'} />
              <DetailRow label="Language" value={b.template_language || 'en_US'} />
              {(b.template_params || []).map((p, i) => (
                <DetailRow key={i} label={`Param {{${i + 1}}}`} value={
                  <>
                    {p || <em style={{ color: 'var(--text-muted)' }}>empty</em>}
                    {(b.template_param_fallbacks || [])[i] && (
                      <span style={{ color: 'var(--text-muted)' }}> · fallback: {b.template_param_fallbacks[i]}</span>
                    )}
                  </>
                } />
              ))}
            </>
          ) : (
            <>
              <DetailRow label="Subject" value={b.subject || '—'} />
              {b.email_header_image && (
                <DetailRow label="Header image" value={<a href={b.email_header_image} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-light)' }}>view</a>} />
              )}
              {b.email_header_video && (
                <DetailRow label="Header video" value={<a href={b.email_header_video} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-light)' }}>view</a>} />
              )}
              {b.text_body && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Body</div>
                  <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', maxHeight: 140, overflowY: 'auto', background: 'var(--surface2)', borderRadius: 8, padding: 10 }}>
                    {b.text_body}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Stats tiles — click to filter the table */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        {tiles.map(tile => (
          <button key={tile.label} disabled={!tile.key}
            onClick={() => tile.key && setFilter(filter === tile.key ? 'all' : tile.key)}
            style={{
              padding: '10px 18px', borderRadius: 8, minWidth: 90, textAlign: 'center',
              border: `2px solid ${tile.key && filter === tile.key ? 'var(--primary)' : 'transparent'}`,
              background: 'var(--surface2)', cursor: tile.key ? 'pointer' : 'default',
            }}>
            <div style={{ fontWeight: 700, fontSize: 20 }}>{tile.value}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{tile.label}</div>
          </button>
        ))}
      </div>

      {/* Recipients */}
      <div className="card">
        <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <span>Recipients — {visible.length}{filter !== 'all' ? ` ${activeFilter.label.replace(/^\S+\s/, '')}` : ''}</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {STATUS_FILTERS.map(f => (
                <button key={f.key}
                  className={`btn btn-sm ${filter === f.key ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setFilter(f.key)}>
                  {f.label}
                </button>
              ))}
            </div>
            <input className="input" style={{ width: 200 }} placeholder="Search name, phone, email…"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        {visible.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 24 }}>
            No recipients match this filter.
          </div>
        ) : (
          <div style={{ maxHeight: 480, overflowY: 'auto', borderRadius: 8, border: '1px solid var(--border)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1 }}>
                <tr>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Phone / Email</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Attempts</th>
                  <th style={thStyle}>Sent</th>
                  <th style={thStyle}>Delivered</th>
                  <th style={thStyle}>Read</th>
                  <th style={thStyle}>Clicked</th>
                  <th style={thStyle}>Error</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(51,65,85,0.4)' }}>
                    <td style={tdStyle}>{r.contact?.name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>{r.contact?.phone || r.contact?.email || '—'}</td>
                    <td style={tdStyle}><span className={`badge ${STATUS_BADGE[r.status] || 'badge-gray'}`}>{r.status}</span></td>
                    <td style={tdStyle}>{r.attempts || 0}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-muted)', fontSize: 12 }}>{fmt(r.sent_at)}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-muted)', fontSize: 12 }}>{fmt(r.delivered_at)}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-muted)', fontSize: 12 }}>{fmt(r.read_at)}</td>
                    <td style={tdStyle}>{r.clicked ? <span title={fmt(r.clicked_at)}>🔗 yes</span> : '—'}</td>
                    <td style={{ ...tdStyle, color: 'var(--danger)', fontSize: 12, maxWidth: 220 }}>{r.error || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '5px 0', fontSize: 13, borderBottom: '1px solid rgba(51,65,85,0.25)' }}>
      <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}

const thStyle = {
  padding: '8px 12px', textAlign: 'left',
  borderBottom: '1px solid var(--border)',
  color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase',
};
const tdStyle = { padding: '8px 12px' };
